import React, { useRef } from 'react'
import cx from 'classnames'

import Icon, { EIconId } from '../../../../components/icons'

import { TCampaignItem, TNavItem } from '../../../../types/mainNavigation'
import TextLink from '../../../../components/TextLink/TextLink'
import { appendNavParameter } from '../../../../utils/buildUrlFromSuggestion'
import { useCallbackWhenVisible } from '../../../../utils/gtm/useCallbackWhenVisible'
import trackEvent from '../../../../utils/gtm/trackEvent'
import { getEecPromoClickEventForCampaign } from '../../../../utils/gtm/eecPromoClickEvents'
import { getEecPromoImpressionEventForCampaign } from '../../../../utils/gtm/eecPromoImpressionEvents'

type Transitions = 'STARTUP' | 'CLOSED_L1' | 'L1_CLOSED' | 'L1_L2' | 'L2_L1' | 'L2_CLOSED'

const mapStateAllContainer: Record<Transitions, string> = {
  STARTUP: 'transition-transform duration-300 -translate-x-full',
  CLOSED_L1: 'transition-transform duration-300 translate-x-0',
  L1_CLOSED: 'transition-transform duration-300 -translate-x-full',
  L1_L2: '',
  L2_L1: '',
  L2_CLOSED: 'transition-transform duration-300 -translate-x-full',
}

const mapStateL1: Record<Transitions, string> = {
  STARTUP: '',
  CLOSED_L1: '',
  L1_CLOSED: '',
  L1_L2: 'transition-transform duration-300 -translate-x-full',
  L2_L1: 'transition-transform duration-300 translate-x-0',
  L2_CLOSED: 'uif-left-260', //  we dont used hidden as it prevents the animation working
}

const mapStateL2: Record<Transitions, string> = {
  STARTUP: '',
  CLOSED_L1: 'uif-left-260 transition-transform duration-300 translate-x-full',
  L1_CLOSED: 'hidden',
  L1_L2: 'transition-transform duration-300 translate-x-0',
  L2_L1: 'transition-transform duration-300 translate-x-full',
  L2_CLOSED: '',
}

const MenuCampaign: React.FC<{ campaign: TCampaignItem }> = (props) => {
  const campaign = props.campaign
  const ref = useRef(null)

  useCallbackWhenVisible(ref, (pathName) => trackEvent(getEecPromoImpressionEventForCampaign(campaign, pathName)))

  return (
    <div ref={ref} onClick={() => trackEvent(getEecPromoClickEventForCampaign(campaign, window.location.pathname))}>
      <p className="px-16 mt-24 text-20 leading-24 font-bold py-12">Aktuelles Highlight</p>
      <a
        className={cx('flex flex-col justify-center px-16 bg-cover mx-auto items-start', campaign?.textColor)}
        href={campaign?.targetUrl}
        id={campaign?.trackingId}
        style={{ marginBottom: '7rem' }}
      >
        <div
          className={cx('bg-lightgrey-light bg-cover', campaign?.textColor)}
          style={{
            ...(campaign?.backgroundColor ? { backgroundColor: campaign?.backgroundColor } : {}),
            ...(campaign?.imageUrl
              ? {
                  backgroundImage: `url(${campaign?.imageUrl})`,
                  backgroundPosition: 'right',
                }
              : {}),
            height: '10.5rem',
            width: '100%',
          }}
        />
        {campaign?.label && <span className="my-8 font-bold text-20 leading-24 text-dark">{campaign?.label}</span>}
        <TextLink text="Jetzt ansehen" className="text-dark mb-16" />
      </a>
    </div>
  )
}

const MobileNavigation: React.FC<{
  navItems: TNavItem[]
  menuClosed: boolean
}> = (props) => {
  const [currentIndex, setCurrentIndex] = React.useState<number>(-1)
  const [startUp, setStartUp] = React.useState<boolean>(true)
  const [transition, setTransition] = React.useState<Transitions>('STARTUP')

  // we want to track the previous state of a prop
  // taken from https://blog.logrocket.com/how-to-get-previous-props-state-with-react-hooks/
  function usePrevious(value: boolean) {
    const ref = React.useRef<boolean>()
    React.useEffect(() => {
      ref.current = value
    })
    return ref.current
  }

  const prevMenuClosed = usePrevious(props.menuClosed)

  // its necessary to track all dependencies to avoid bad side-effects:
  // https://reactjs.org/docs/hooks-faq.html#performance-optimizations
  React.useEffect(() => {
    const setNotStartup = () => {
      if (startUp) {
        setStartUp(!startUp)
      }
    }

    // closing the menu
    if (!prevMenuClosed && props.menuClosed) {
      if (transition === 'CLOSED_L1') {
        setTransition('L1_CLOSED')
        setCurrentIndex(-1)
      } else if (transition === 'L1_L2') {
        setTransition('L2_CLOSED')
      } else if (transition === 'L2_L1') {
        setTransition('L1_CLOSED')
        setCurrentIndex(-1)
      }
    } // opening the menu
    else if (prevMenuClosed && !props.menuClosed) {
      if (transition === 'STARTUP') {
        setNotStartup()
        setTransition('CLOSED_L1')
      } else if (transition === 'L1_CLOSED') {
        setTransition('CLOSED_L1')
      } else if (transition === 'L2_CLOSED') {
        setTransition('CLOSED_L1')
        setCurrentIndex(-1)
      }
    }
  }, [props.menuClosed, prevMenuClosed, startUp, transition])

  const commonMenuClasses = 'absolute z-30 bg-white text-18 lg:hidden'

  const tNavItem = props.navItems[currentIndex]

  return (
    <div
      className={cx(
        // this moves the whole menu area away from the menü button or over it
        'bg-white', // an iphone fix
        'absolute z-20 top-0 h-screen uif-w-260',
        mapStateAllContainer[transition],
      )}
    >
      <div
        className={cx(
          // this is used to make only one level visible at once
          'absolute w-full h-screen overflow-x-hidden top-0 z-20 overflow-x-hidden',
          { 'overflow-hidden': transition === 'L2_L1' || transition === 'CLOSED_L1' },
        )}
      >
        <div className={cx(commonMenuClasses, 'top-0 uif-w-260 px-16 h-screen overflow-auto', mapStateL1[transition])}>
          <div className="flex -mr-4 py-8">
            <p className="relative font-bold text-20">Menü</p>
          </div>
          <ul>
            {props.navItems.map((item, index) => {
              return (
                <li className={cx('border-b border-lightgrey-light -mb-px')} key={item.label}>
                  <NavigationLink
                    className={cx('block text-16 py-12', {
                      'text-sale-red': item.isHighlight,
                      'font-bold': currentIndex === index,
                    })}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault()
                      setCurrentIndex(index)
                      setTransition('L1_L2')
                    }}
                    label={item.label}
                    url={item.url}
                    id={item.trackingId}
                    icon={item.icon}
                    isOpen={currentIndex === index}
                    isExternal={item.isExternal}
                    hasArrow={true}
                  />
                </li>
              )
            })}
          </ul>
        </div>
        <div className={cx(commonMenuClasses, 'uif-w-260', mapStateL2[transition])}>
          <ul>
            {props.navItems.map((item, index) => {
              const flyoutNavItem = item.flyoutNavItem
              return (
                <li
                  className={cx('border-t border-lightgrey-light -mb-px', { hidden: currentIndex !== index })}
                  key={item.label}
                >
                  <NavigationLink
                    className={cx('block text-20 py-8', {
                      'text-sale-red': item.isHighlight,
                      'font-bold': currentIndex === index,
                    })}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault()
                      setCurrentIndex(index)
                      setTransition('L2_L1')
                    }}
                    label={item.label}
                    url={item.url}
                    id={item.trackingId}
                    icon={item.icon}
                    isOpen={currentIndex === index}
                    isExternal={item.isExternal}
                    hasArrow={true}
                    arrowLeft={true}
                  />
                  {flyoutNavItem.categories.length > 0 && currentIndex === index && (
                    <ul className="pl-16">
                      {flyoutNavItem.categories.map((subItem) => {
                        return (
                          <li key={subItem.label} className="border-t border-lightgrey-light text-16">
                            <NavigationLink
                              className="py-12"
                              onClick={(e: React.MouseEvent) => {
                                e.preventDefault()
                                setTransition('L2_CLOSED')
                              }}
                              label={subItem.label}
                              id={subItem.trackingId}
                              url={subItem.url}
                              isExternal={true}
                            />
                          </li>
                        )
                      })}
                      {flyoutNavItem.categoryLabelUrl && flyoutNavItem.categoryLabel && (
                        <li className="border-t border-lightgrey-light">
                          <TextLink
                            text={flyoutNavItem.categoryLabel}
                            className="inline-block py-12"
                            href={flyoutNavItem.categoryLabelUrl}
                            id={flyoutNavItem.categoryLabelId}
                          />
                        </li>
                      )}
                    </ul>
                  )}
                </li>
              )
            })}
          </ul>

          {currentIndex > -1 && tNavItem.flyoutNavItem.brands.length > 0 && (
            <>
              <p className="px-16 mt-24 text-20 font-bold py-8">Top Marken</p>
              <ul className="pl-16 py-8 text-16">
                {tNavItem.flyoutNavItem.brands.map((brand) => (
                  <li className="border-t border-lightgrey-light" key={brand.label}>
                    <NavigationLink
                      className="py-8"
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault()
                        setTransition('L2_CLOSED')
                      }}
                      label={brand.label}
                      url={brand.url}
                      id={brand.trackingId}
                      isExternal={true}
                    />
                  </li>
                ))}
                {tNavItem.flyoutNavItem.brandLabelUrl && (
                  <li className="border-t border-lightgrey-light">
                    <TextLink
                      text="Marken entdecken"
                      className="inline-block py-12"
                      href={tNavItem.flyoutNavItem.brandLabelUrl}
                      id={tNavItem.flyoutNavItem.brandLabelId}
                    />
                  </li>
                )}
              </ul>
            </>
          )}

          {currentIndex > -1 && tNavItem.flyoutNavItem.campaign?.targetUrl && (
            <MenuCampaign campaign={tNavItem.flyoutNavItem.campaign} />
          )}
        </div>
      </div>
    </div>
  )
}

export const NavigationLink: React.FC<{
  label?: string
  url: string
  id?: string
  icon?: string
  isOpen?: boolean
  isExternal?: boolean
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
  className?: string
  hasArrow?: boolean
  arrowLeft?: boolean
}> = (props) => {
  return (
    <a
      className={cx('flex items-center', props.className)}
      href={props.isExternal ? appendNavParameter(props.url) : undefined}
      onClick={props.isExternal ? undefined : props.onClick}
      id={props.id}
    >
      {props.hasArrow && props.arrowLeft && <Icon id={EIconId.ANGLE_RIGHT} className="w-24 rotate-180" />}
      {props.label}
      {props.hasArrow && !props.arrowLeft && <Icon id={EIconId.ANGLE_RIGHT} className="w-24 ml-auto" />}
    </a>
  )
}

export default MobileNavigation
